import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { STORAGE_PREFIX } from '../../config/env';

export type Tema = 'oscuro' | 'claro';

interface EstadoTema {
  tema: Tema;
  /**
   * Si la persona ha elegido el tema a mano.
   *
   * Mientras sea falso, la consola sigue al sistema: cambia con él al
   * atardecer o al pulsar el conmutador del móvil. En cuanto alguien toca el
   * botón, su decisión manda y deja de seguirlo — respetar la preferencia del
   * dispositivo no puede significar deshacer lo que el usuario acaba de pedir.
   */
  elegidoPorUsuario: boolean;
  alternar: () => void;
  establecer: (tema: Tema) => void;
}

const CONSULTA_OSCURO = '(prefers-color-scheme: dark)';

/** Qué tema tiene puesto el dispositivo ahora mismo. */
function temaDelSistema(): Tema {
  // En un entorno sin `matchMedia` —una prueba, un renderizado en servidor— se
  // supone claro, que es lo que había antes de todo esto.
  if (typeof window === 'undefined' || !window.matchMedia) return 'claro';
  return window.matchMedia(CONSULTA_OSCURO).matches ? 'oscuro' : 'claro';
}

/** Aplica el tema al documento. El CSS reacciona al atributo `data-theme`. */
function aplicar(tema: Tema): void {
  document.documentElement.dataset.theme = tema === 'claro' ? 'light' : 'dark';
}

export const useTemaStore = create<EstadoTema>()(
  persist(
    (set, get) => ({
      /*
       * Arranca con lo que tenga el dispositivo.
       *
       * Antes arrancaba siempre en claro, así que quien tiene el sistema en
       * oscuro se llevaba un fogonazo blanco en cada visita y tenía que
       * cambiarlo a mano.
       */
      tema: temaDelSistema(),
      elegidoPorUsuario: false,

      alternar: () => {
        const nuevo: Tema = get().tema === 'oscuro' ? 'claro' : 'oscuro';
        aplicar(nuevo);
        set({ tema: nuevo, elegidoPorUsuario: true });
      },

      establecer: (tema) => {
        aplicar(tema);
        set({ tema, elegidoPorUsuario: true });
      },
    }),
    {
      name: `${STORAGE_PREFIX}tema`,
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (estado) => {
        if (!estado) return;

        /*
         * Lo guardado sólo manda si fue una decisión de la persona. Si no,
         * vuelve a mirarse el sistema: entre dos visitas el dispositivo puede
         * haber cambiado de tema y lo que hay en el almacén sería lo de antes.
         */
        if (!estado.elegidoPorUsuario) {
          const delSistema = temaDelSistema();
          aplicar(delSistema);
          useTemaStore.setState({ tema: delSistema });
          return;
        }

        aplicar(estado.tema);
      },
    },
  ),
);

/*
 * Seguir al sistema mientras nadie haya elegido.
 *
 * Se engancha una sola vez al cargar el módulo, no en un componente: el tema
 * es del documento entero y no de una pantalla, y así no depende de que algo
 * concreto esté montado.
 */
if (typeof window !== 'undefined' && window.matchMedia) {
  window.matchMedia(CONSULTA_OSCURO).addEventListener('change', (evento) => {
    if (useTemaStore.getState().elegidoPorUsuario) return;

    const nuevo: Tema = evento.matches ? 'oscuro' : 'claro';
    aplicar(nuevo);
    useTemaStore.setState({ tema: nuevo });
  });
}
